import type { ReactElement } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { cn, getUserDisplayName } from '@/lib/utils';
import { UserTypeTag } from './UserTypeTag';

export interface UsersCellProps {
  alert: GroupedAlert;
  /** When set, the cell is clickable and opens the users dialog. */
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Table cell that shows alert user count or single user name.
 * When one user: shows the user's display name. When multiple: "N users" with tooltip listing names.
 * When onClick is provided, cell is clickable to open AlertUsersDialog.
 */
export function UsersCell({ alert, onClick }: UsersCellProps): ReactElement {
  const { users } = alert;
  const isSingleUser = users.length === 1;
  const label = isSingleUser
    ? getUserDisplayName(users[0])
    : `${users.length} users`;
  const namesList = users.map(getUserDisplayName).join('\n');
  const isClickable = !!onClick;

  // KIR-3908: the user type tag (Discovered / No email) sits on its OWN row
  // underneath the user email, so a long email doesn't get crowded by the chip
  // and the "Discovered" state reads as a label about the row above it.
  const trigger = (
    <span
      className={cn(
        'flex flex-col items-start gap-0.5 min-w-0',
        isClickable ? 'cursor-pointer hover:text-foreground' : 'cursor-default'
      )}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                (onClick as (e: unknown) => void)(e);
              }
            }
          : undefined
      }
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <span className="flex items-center gap-0.5 min-w-0 max-w-full">
        <span className="truncate underline decoration-dotted underline-offset-2">
          {label}
        </span>
        {isClickable && <ChevronRight className="h-3 w-3 flex-shrink-0" />}
      </span>
      {isSingleUser && <UserTypeTag userType={users[0].userType} />}
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm whitespace-pre-line">
        {namesList}
      </TooltipContent>
    </Tooltip>
  );
}

UsersCell.displayName = 'UsersCell';
